import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { Button, Icon, Menu, PopupDirection, RadioBox } from 'lego-on-react';
import { compact, get } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { keyDownManager } from '../../service/KeyDownManager/KeyDownManager';
import { TestBem } from '../../service/TestBem';
import { DropdownChooserItem, KeyCode } from '../../typings/common';
import { CheckboxDecorator } from '../CheckboxDecorator/CheckboxDecorator';
import { ContainerOverflow } from '../ContainerOverflow/ContainerOverflow';
import { HoverTooltipWrapper } from '../HoverTooltipWrapper/HoverTooltipWrapper';
import { Loader } from '../Loader/Loader';
import { ScopedPopup } from '../ScopedPopup/ScopedPopup';
import { TextInputDecorator } from '../TextInputDecorator/TextInputDecorator';

import './DropdownChooser.css';
import * as keyset from './DropdownChooser.i18n';

const i18n = i18nFactory(keyset);

const block = 'dropdown-chooser';

export enum DropdownChooserView {
    classic = 'classic',
    withControls = 'withControls'
}

interface DropdownChooserMessage {
    title: string;
    detail?: string;
}

interface DropdownChooserBaseProps {
    cls?: string;
    popupCls?: string;
    size: 's' | 'n';
    view: DropdownChooserView;
    menuItems?: DropdownChooserItem[];
    labelButton: React.ReactNode;
    onChange: (val: string) => void;
    searchFilter: {
        isEnabled: boolean;
        labelSearch?: string;
        emptySearchResultMessage?: DropdownChooserMessage;
    };
    emptyListMessage?: DropdownChooserMessage;
    onOpen?: () => void;
    onClose?: () => void;
    width?: number;
    hasErrors?: boolean;
    disabled?: boolean;
    directions?: PopupDirection[];
    applyButton?: {
        text: string;
        disabled: boolean;
        disabledHint?: string;
        onClick: () => void;
    };
    footerContent?: React.ReactNode;
}

interface DropdownChooserSingleSelectProps extends DropdownChooserBaseProps {
    isMulti: false;
    value?: string;
}

interface DropdownChooserMultiSelectProps extends DropdownChooserBaseProps {
    isMulti: true;
    value?: string[];
}

export type DropdownChooserProps = DropdownChooserMultiSelectProps | DropdownChooserSingleSelectProps;

interface DropdownChooserState {
    isOpen: boolean;
    search: string;
}

export class DropdownChooser extends React.PureComponent<DropdownChooserProps, DropdownChooserState> {
    private button: Button | null;
    private buttonContainer: HTMLDivElement | null;
    private keyDownManagerId: string;

    /**
     * Эскейпит специальные символы регэкспов
     * @param text
     */
    static escapeRegExp(text: string): string {
        return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    static getEmptyMessage({ title, detail }: DropdownChooserMessage): React.ReactNode {
        return (
            <div className={Bem.cls(block, 'message')}>
                <div className={Bem.cls(block, 'message-title')}>{title}</div>

                {detail ? <div className={Bem.cls(block, 'message-detail')}>{detail}</div> : null}
            </div>
        );
    }

    constructor(props: DropdownChooserProps) {
        super(props);

        this.state = {
            isOpen: false,
            search: ''
        };
    }

    render() {
        const {
            labelButton,
            cls,
            width = get(this.buttonContainer, 'clientWidth', 460),
            hasErrors,
            disabled,
            size,
            directions
        } = this.props;
        const { isOpen } = this.state;

        return (
            <div
                className={classNames(
                    Bem.cls({
                        block: block,
                        mods: { error: Boolean(hasErrors) }
                    }),
                    cls
                )}
                ref={ref => (this.buttonContainer = ref)}
            >
                <Button
                    theme="normal"
                    view="classic"
                    size={size}
                    cls={classNames(Bem.cls(block, 'button'), TestBem.cls(block, 'button'))}
                    iconRight={<Icon size={'n'} type={'arrow'} direction={isOpen ? 'top' : 'bottom'} />}
                    onClick={this.toggleVisible}
                    disabled={disabled}
                    ref={ref => (this.button = ref)}
                >
                    {labelButton}
                </Button>

                <ScopedPopup
                    mainOffset={this.getMainOffset()}
                    theme="normal"
                    hiding={true}
                    target={'anchor'}
                    anchor={this.button}
                    visible={isOpen}
                    onOutsideClick={this.closePopup}
                    directions={directions}
                >
                    <div
                        className={Bem.cls({
                            block: block,
                            elem: 'dropdown-content',
                            mods: { size: size }
                        })}
                        style={{ width: width }}
                    >
                        {this.getPopupContent()}
                    </div>
                </ScopedPopup>
            </div>
        );
    }

    componentDidMount() {
        this.keyDownManagerId = keyDownManager.on([[KeyCode.ESC, this.toggleVisible]], false);
    }

    componentWillUnmount() {
        keyDownManager.off(this.keyDownManagerId);
    }

    componentDidUpdate(prevProps: DropdownChooserProps, prevState: DropdownChooserState) {
        if (prevState.isOpen !== this.state.isOpen) {
            keyDownManager.turn(this.keyDownManagerId, this.state.isOpen);
        }
    }

    toggleVisible = () => {
        const { onOpen, onClose } = this.props;

        if (!this.state.isOpen && onOpen) {
            onOpen();
        } else if (onClose) {
            onClose();
        }

        this.setState({
            isOpen: !this.state.isOpen
        });
    };

    private closePopup = () => {
        const { onClose } = this.props;

        if (onClose) {
            onClose();
        }

        this.setState({
            isOpen: false
        });
    };

    private getPopupContent = () => {
        const {
            menuItems,
            popupCls,
            value,
            isMulti,
            emptyListMessage = { title: i18n('no-variants') },
            searchFilter,
            size
        } = this.props;

        if (menuItems === undefined) {
            return (
                <div className={Bem.cls(block, 'loader')}>
                    <Loader size={'s'} />
                </div>
            );
        }

        if (menuItems.length === 0) {
            return (
                <div>
                    {DropdownChooser.getEmptyMessage(emptyListMessage)}

                    {this.getFooterContent()}
                </div>
            );
        }

        const filteredList = this.getFilteredList(menuItems);
        const isFilteredListEmpty = filteredList.length === 0;

        return (
            <div className={classNames(Bem.cls(block, 'wrapper'), popupCls)}>
                {this.getSearchFilter()}

                <ContainerOverflow cls={Bem.cls(block, 'container-overflow')}>
                    {searchFilter.isEnabled && isFilteredListEmpty ? (
                        DropdownChooser.getEmptyMessage(
                            searchFilter.emptySearchResultMessage || {
                                title: i18n('empty-search-result-title'),
                                detail: i18n('empty-search-result-details')
                            }
                        )
                    ) : (
                        <Menu
                            cls={Bem.cls(block, 'menu')}
                            theme="dropdown-chooser"
                            size={size}
                            type={isMulti ? 'check' : 'radiocheck'}
                            onClick={this.onMenuItemClick}
                            val={value}
                        >
                            {filteredList}
                        </Menu>
                    )}
                </ContainerOverflow>

                {this.getFooterContent()}
            </div>
        );
    };

    private getFooterContent = () => {
        const { applyButton, footerContent, size } = this.props;

        if (applyButton === undefined && footerContent === undefined) {
            return null;
        }

        let applyButtonContent;

        if (applyButton) {
            applyButtonContent = (
                <HoverTooltipWrapper
                    to={'right'}
                    tooltipText={
                        applyButton.disabled && applyButton.disabledHint ? applyButton.disabledHint : undefined
                    }
                >
                    <Button
                        theme="normal"
                        view="classic"
                        size={size}
                        disabled={applyButton.disabled}
                        onClick={this.onApplyButtonClick}
                    >
                        {applyButton.text}
                    </Button>
                </HoverTooltipWrapper>
            );
        }

        return (
            <div className={Bem.cls(block, 'footer')}>
                {applyButtonContent}

                {footerContent}
            </div>
        );
    };

    private onApplyButtonClick = () => {
        const { applyButton } = this.props;

        if (applyButton === undefined) {
            return;
        }

        applyButton.onClick();
        this.closePopup();
    };

    private getSearchFilter = () => {
        const { searchFilter, size } = this.props;
        const { search } = this.state;

        if (!searchFilter.isEnabled) {
            return null;
        }

        return (
            <div className={Bem.cls(block, 'search-wrap')}>
                <TextInputDecorator
                    hasClear={true}
                    cls={Bem.cls(block, 'search')}
                    placeholder={searchFilter.labelSearch || i18n('search')}
                    onChange={this.onSearchChange}
                    size={size}
                    theme={'normal'}
                    text={search}
                />
            </div>
        );
    };

    private getFilteredList = (menuItems: DropdownChooserItem[]) => {
        if (this.state.search) {
            menuItems = menuItems.filter(item => !item.isDisabled);
        }

        return compact(menuItems.map(this.getMenuItem, this));
    };

    private getMenuItem = (data: DropdownChooserItem) => {
        const { search } = this.state;
        const { name, id, addition, isDisabled, nameCls } = data;
        const searchRegExp = search ? new RegExp(DropdownChooser.escapeRegExp(search), 'gi') : null;
        const highlightedName = this.highlight(name, searchRegExp);
        const highlightedAddition = addition ? this.highlight(addition, searchRegExp) : addition;

        if (search && highlightedName === name && highlightedAddition === addition) {
            return null;
        }

        return (
            <Menu.Item
                val={id}
                key={id}
                cls={classNames(
                    Bem.cls(block, 'menu-item', {
                        disabled: Boolean(isDisabled)
                    }),
                    TestBem.cls(block, 'menu-item')
                )}
            >
                <span className={Bem.cls(block, 'menu-item-content')}>
                    {this.getMenuItemChecker(data)}

                    <span
                        className={classNames(Bem.cls(block, 'name'), TestBem.cls(block, 'name'), nameCls)}
                        title={name}
                    >
                        {highlightedName}
                    </span>

                    {highlightedAddition ? (
                        <span className={Bem.cls(block, 'addition')}>{highlightedAddition}</span>
                    ) : null}
                </span>
            </Menu.Item>
        );
    };

    private getMenuItemChecker = (data: DropdownChooserItem) => {
        const { view } = this.props;

        switch (view) {
            case DropdownChooserView.classic:
                return this.getClassicMenuItemChecker(data);

            case DropdownChooserView.withControls:
                return this.getControlMenuItemChecker(data);

            default:
                return null;
        }
    };

    private getControlMenuItemChecker = (data: DropdownChooserItem) => {
        const { value = [], isMulti, size } = this.props;
        const { id, isDisabled } = data;

        if (isMulti) {
            return (
                <CheckboxDecorator
                    cls={Bem.cls(block, 'checker')}
                    disabled={isDisabled}
                    size={size}
                    theme="normal"
                    checked={value.includes(id)}
                />
            );
        }

        return (
            <RadioBox size={size} theme="normal" disabled={isDisabled} cls={Bem.cls(block, 'checker')}>
                <RadioBox.Radio checked={value === id} />
            </RadioBox>
        );
    };

    private getClassicMenuItemChecker = (data: DropdownChooserItem) => {
        const { id } = data;

        if (this.props.value === undefined) {
            return null;
        }

        const values = this.props.isMulti ? this.props.value : [this.props.value];
        const isChecked = values.includes(id);

        return isChecked ? <Icon glyph="type-check" cls={Bem.cls(block, 'checker')} /> : null;
    };

    // marfuzzi@todo вынести функцию highlight
    private highlight(text: string, searchRegExp: RegExp | null): React.ReactNode {
        if (!searchRegExp) {
            return text;
        }

        const mark = '__MARK__';
        const markedText = text.replace(searchRegExp, (res: string) => `${mark}${res}${mark}`);

        return markedText === text
            ? text
            : markedText.split(mark).map((part: string, i: number) =>
                  i % 2 ? (
                      <mark key={i} className={Bem.cls(block, 'mark')}>
                          {part}
                      </mark>
                  ) : (
                      part
                  )
              );
    }

    private onMenuItemClick = (event: React.FormEvent<HTMLElement>, val: string) => {
        const { isMulti } = this.props;
        const { onChange } = this.props;

        if (!isMulti) {
            this.setState({ isOpen: false });
        }

        onChange(val);
    };

    private onSearchChange = (value: string) => {
        this.setState({
            search: value
        });
    };

    private getMainOffset = () => {
        const { size } = this.props;

        switch (size) {
            case 'n':
                return 5;
            case 's':
            default:
                return 0;
        }
    };
}
